/* eslint-disable @typescript-eslint/naming-convention */
import { CommonModule } from '@angular/common';
import { Component, Inject, Injector, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GlobalConst } from '@app/core/constants/global.const';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { DeviceDetectService } from '@app/core/services/device-detect/device-detect.service';
import { CustomService } from '@app/core/services/drupal8/custom/custom.service';
import { LoggerService } from '@app/core/services/logger/logger.service';
import { ModalTypes } from '@app/core/services/modal-manager';
import { ModalComponent } from '@app/core/services/modal-manager/modal-component';
import { NavDataService } from '@app/core/services/nav-data/nav-data.service';
import { NotificationsService } from '@app/core/services/notifications/notifications.service';
import { TrackingService } from '@app/core/services/tracking/tracking.service';
import { CustomValidators } from '@app/core/validators/custom-validators';
import { environment } from '@env/environment';
import { IAPProduct, InAppPurchase2 } from '@ionic-native/in-app-purchase-2/ngx';
import {
  CartOrder,
  CommercePayment,
  CommercePaymentEntity,
  CommerceService,
  DrupalConstants,
  FlagService,
} from '@makiwin/ngx-drupal8-rest';
import { TranslateService } from '@ngx-translate/core';
import {
  PaymentRequestOptions,
  StripeCardElementChangeEvent,
  StripeCardElementOptions,
  StripeElementLocale,
  StripeElementsOptions,
} from '@stripe/stripe-js';
import { IClientAuthorizeCallbackData, IPayPalConfig, ITransactionItem, NgxPayPalModule } from 'ngx-paypal';
import { NgxStripeModule, StripeCardComponent, StripeService } from 'ngx-stripe';
import { Observable, from } from 'rxjs';
import { SharedModule } from '../../shared.module';
@Component({
  standalone: true,
  selector: 'payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  imports: [CommonModule, NgxPayPalModule, NgxStripeModule, FormsModule, ReactiveFormsModule, SharedModule],
})
export class PaymentComponent extends ModalComponent implements OnInit {
  @ViewChild('stripeCard') stripeCard?: StripeCardComponent;
  readonly buyStripeUrl = GlobalConst.buyStripeUrl;
  declare extraData: CartOrder;
  paymentForm?: FormGroup;

  cardValid = false;
  payPalConfig: IPayPalConfig = {
    clientId: environment.paypalClientId,
    advanced: {
      commit: 'false',
    },
    style: {
      label: 'paypal',
      layout: 'horizontal',
      tagline: false,
    },
  };
  cardError?: Error;
  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#276fd3',
        color: '#32325d',
        lineHeight: '40px',
        fontWeight: '300',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    },
    hidePostalCode: true,
  };

  elementsOptions: StripeElementsOptions = {
    locale: this.translate.currentLang as StripeElementLocale,
  };

  requestOptions: PaymentRequestOptions | any = {
    country: 'US',
    currency: 'usd',
    total: {
      amount: 0,
      label: 'Pay Now',
    },
  };

  displayIap = false;
  stripeAvailable = false;

  stripeService?: StripeService;

  protected readonly canShowBankTransfer = signal(false);
  protected readonly processing = signal(false);
  protected readonly isLoading = signal(true);

  private authSessionService: AuthSessionService = inject(AuthSessionService);
  private trackingService: TrackingService = inject(TrackingService);
  private loggerService: LoggerService = inject(LoggerService);

  constructor(
    public navDataService: NavDataService,
    private formBuilder: FormBuilder,
    private commerceService: CommerceService,
    private customService: CustomService,
    private inAppPurchase2: InAppPurchase2,
    private translate: TranslateService,
    private flagService: FlagService,
    private deviceDetectService: DeviceDetectService,
    private notificationsService: NotificationsService,
    @Inject(Injector) private readonly injector: Injector
  ) {
    super();
  }

  /**
   * Payment modal will always receive a cart order to pay for
   * To purchase any item and create a payment for it, first add the product to cart using CommerceService
   * Then send the order as an extraData to this component
   **/
  ngOnInit() {
    if (!this.extraData.order_id) {
      return;
    }
    this.processing.set(true);
    this.isLoading.set(true);
    this.commerceService.getCart().subscribe((data) => {
      this.extraData = data.find((co) => +co.order_id === +this.extraData.order_id) as any;
      // if order not found
      this.isLoading.set(false);
      if (!this.extraData) {
        return;
      }
      this.canShowBankTransfer.set(
        this.extraData?.order_items?.filter(
          (item) => item.purchased_entity.type === 'membership' || item.purchased_entity.type === 'subscriptions'
        ).length === 0
      );

      this.processing.set(false);
      if (
        this.extraData.order_items[0].purchased_entity.type === 'subscription' &&
        !this.deviceDetectService.iosDisplayed
      ) {
        this.inAppPurchase2.register({
          id: this.extraData.order_items[0].purchased_entity.sku,
          type: this.inAppPurchase2.PAID_SUBSCRIPTION,
        });
        this.displayIap = true;
        this.payWithInAppPurchase();
      }
      this.requestOptions.total.amount = Math.round(+(this.extraData.total_price.number as any));
      this.requestOptions.currency = this.extraData?.total_price.currency_code?.toLowerCase();
      this.buildForm();
      this.initPayPal();

      const entity_type = 'commerce_order';
      const flag_id = 'started_payment';
      this.customService
        .isEntityFlagged(entity_type, this.extraData.order_id.toString(), flag_id)
        .subscribe((flagged) => {
          if (!flagged) {
            this.flagService
              .post({
                flag_id,
                entity_type,
                entity_id: this.extraData.order_id,
                uid: DrupalConstants.Connection?.current_user.uid,
              })
              .subscribe();
          }
        });
    });
  }

  openBankTransferInfo(): void {
    this.modalManager?.open(ModalTypes.bankTransferInfo);
    // this.modalBankTransferInfo.open().subscribe();
  }

  payWithInAppPurchase() {
    this.processing.set(true);
    const sku = ''; //this.extraData.order_items[0].purchased_entity.sku;

    this.inAppPurchase2
      .when(sku)
      .approved((product: IAPProduct) => {
        this.payment();

        this.customService.verifyInAppBrowser(this.extraData.order_id, product).subscribe((data) => {
          if (data) {
            product.verify();
            this.processing.set(false);
          }
        });
      })
      .verified((product: IAPProduct) => {
        product.finish();
        this.processing.set(false);
      })
      .finished(() => {
        this.displayMessageAndClose();
        this.authSessionService.initProfile().subscribe();
      });
    this.inAppPurchase2
      .order(sku)
      .then(() => {
        this.authSessionService.initProfile().subscribe();
      })
      .error((err: any) => {
        console.log(err);
      });
  }

  buildForm() {
    this.paymentForm = this.formBuilder.group({
      termsCheck: [false, [Validators.required, CustomValidators.equal(true)]],
    });
  }

  cardUpdated(result: StripeCardElementChangeEvent) {
    this.cardValid = result.complete;
  }

  requestUpdated(result: any) {
    if (result.paymentMethod) {
      this.processing.set(true);
      this.createPayment(this.extraData.order_id).subscribe((paymentData) => {
        const promise: any = this.stripeService?.confirmCardPayment(
          paymentData.remote_id[0].value,
          { payment_method: result.paymentMethod.id },
          { handleActions: false }
        );
        from(promise).subscribe((confirmResult: any) => {
          if (confirmResult.error) {
            this.logError(confirmResult.error, 'credit-card');
            this.displayMessageAndClose(true);
            result.complete('fail');
          } else {
            result.complete('success');
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const promiseCardPayment: any = this.stripeService?.confirmCardPayment(paymentData.remote_id[0].value);
            from(promiseCardPayment).subscribe((confirmCardResult: any) => {
              if (confirmCardResult.error) {
                this.displayMessageAndClose(true);
                return;
              }
              this.commerceService.capturePayment(this.extraData.order_id, paymentData.payment_id[0].value).subscribe({
                next: () => this.displayMessageAndClose(),
                error: () => {
                  this.notificationsService.notifyError(
                    this.translate.instant('An error occurred confirming your payment')
                  );
                  this.processing.set(false);
                  this.authSessionService.initProfile().subscribe();
                },
              });
            });
          }
        });
      });
    }
  }

  displayMessageAndClose(error = false, message?: string) {
    if (error) {
      if (!message) {
        message = this.translate.instant('Sorry we are not able to process your request. Please try again later.');
      }
      this.notificationsService.notifyError(message as string, { autoHide: false });
      this.processing.set(false);
      return;
    } else {
      this.notificationsService.notifySuccess(this.translate.instant('Thank you for your payment!'), {
        autoHide: false,
      });
      this.modalManager?.dismissAll();
    }
    // WEB-4485
    // WEB-4636: reload user profile to detect role changes when purchasing subscriptions
    this.authSessionService.initProfile().subscribe(() => {
      this.processing.set(false);

      // Reinit the app so old components detect role changes
      this.navDataService.appReady = false;
      setTimeout(() => {
        this.navDataService.appReady = true;
      });

      this.modalManager?.open(ModalTypes.orderSummary, { orderId: this.extraData.order_id }).subscribe(() => {
        const isSubscriptionOrder = this.extraData.order_items.find((oi) => oi.purchased_entity.type === 'membership');
        this.executeNavigation(isSubscriptionOrder ? '/account' : '/shop/orders');
      });
    });
  }

  startPaypalSubscription(agreementId: string) {
    this.customService.startPaypalSubscription(agreementId, this.extraData.order_id).subscribe(
      () => {
        this.displayMessageAndClose();
      },
      (reason) => {
        this.logError(reason.error?.message ?? reason.error, 'credit-card');
        if (reason.error.message) {
          let message = reason.error.message;
          message += ` ${this.translate.instant('Chat with us.')}`;
          this.displayMessageAndClose(true, message);
        } else {
          this.displayMessageAndClose(true);
        }
      }
    );
  }

  paypalClientAuth(authorization: IClientAuthorizeCallbackData) {
    if (authorization.status === 'COMPLETED') {
      this.processing.set(true);
      this.customService.verifyPaypal(this.extraData.order_id, authorization.id).subscribe(
        (confirmData) => {
          this.payment();
          this.displayMessageAndClose(!confirmData);
          this.authSessionService.initProfile().subscribe();
        },
        (err) => {
          this.logError(err, 'paypal');
          this.displayMessageAndClose(true);
        }
      );
    }
  }

  payWithCard() {
    if (
      this.processing() ||
      (this.extraData.total_price.number !== 0 && (!this.paymentForm?.valid || !this.cardValid))
    ) {
      return;
    }
    this.processing.set(true);
    if (this.extraData.total_price.number === 0) {
      this.payment();
      this.commerceService.deleteCartOrderItems(this.extraData.order_id).subscribe();
      this.displayMessageAndClose();
      this.authSessionService.initProfile().subscribe();
      return;
    }

    // WEB-5425: call /api/subscriptions/stripe/start if membership item
    const isSubscriptionOrder = this.extraData.order_items.find((oi) => oi.purchased_entity.type === 'membership');
    if (isSubscriptionOrder) {
      this.submitCardSubscription();
      return;
    }

    this.createPayment(this.extraData.order_id).subscribe({
      next: (paymentData) => {
        this.stripeService
          ?.confirmCardPayment(paymentData.remote_id[0].value, {
            payment_method: {
              card: this.stripeCard?.element as any,
            },
            setup_future_usage: 'off_session',
          })
          .subscribe({
            next: (result) => {
              if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
                this.capturePayment(paymentData.payment_id[0].value);
              } else {
                this.logError(result.error?.message ?? result.error, 'credit-card');
                if (result.error?.message) {
                  let message = result.error.message;
                  message += ` ${this.translate.instant('Chat with us.')}`;
                  this.displayMessageAndClose(true, message);
                } else {
                  this.displayMessageAndClose(true);
                }
              }
            },
            error: (err) => {
              this.processing.set(false);
              this.logError(err.message ?? err, 'credit-card');
              this.notificationsService.notifyError(err.message ?? this.translate.instant('Error during the payment'));
            },
          });
      },
      error: (err) => {
        this.processing.set(false);
        this.logError(err.message ?? err, 'credit-card');
        this.notificationsService.notifyError(err.message ?? this.translate.instant('Error during the payment'));
      },
    });
  }

  /**
   * Start stripe card subscription
   */
  submitCardSubscription() {
    if (!this.stripeCard) {
      return;
    }
    this.stripeService
      ?.createPaymentMethod({
        type: 'card',
        card: this.stripeCard.element,
      })
      .subscribe((result) => {
        const paymentMethodId = result.paymentMethod?.id;
        if (paymentMethodId) {
          this.customService.startStripeSubscription(paymentMethodId, this.extraData.order_id).subscribe(
            () => {
              this.displayMessageAndClose();
            },
            (reason) => {
              this.logError(reason.error?.message ?? reason.error, 'credit-card');
              if (reason.error.message) {
                let message = reason.error.message;
                message += ` ${this.translate.instant('Chat with us.')}`;
                this.displayMessageAndClose(true, message);
              } else {
                this.displayMessageAndClose(true);
              }
            }
          );
        } else {
          this.displayMessageAndClose(true);
        }
      });
  }

  /**
   * Capture (Verify) payment status
   * @param paymentId payment entity id
   */
  capturePayment(paymentId: number) {
    this.commerceService.capturePayment(this.extraData.order_id, paymentId).subscribe({
      next: () => {
        this.displayMessageAndClose();
        this.authSessionService.initProfile().subscribe();
      },
      error: () => {
        this.notificationsService.notifyError(this.translate.instant('An error occurred confirming your payment'));
        this.processing.set(false);
      },
    });
  }

  /**
   * Create stripe decoupled credit card payment and get remote (client_secret) id
   * @param orderId cart order id
   */
  createPayment(orderId: number): Observable<CommercePaymentEntity> {
    const payment: CommercePayment = {
      // decoupled_stripe_recurring
      gateway: 'decoupled_stripe',
      type: 'credit_card',
    };

    return this.commerceService.createPayment(orderId, payment);
  }

  /**
   * WEB-5386: The payment request is not supported in Firefox but the form actually works
   * However only the available methods will be loaded by stripe if they are supported anyway
   * @info https://developer.mozilla.org/en-US/docs/Web/API/PaymentRequest/PaymentRequest
   */
  async initStripe() {
    // const canMakePayment = await this.stripeService?.paymentRequest(this.requestOptions).canMakePayment();
    // if (canMakePayment) {
    //
    this.stripeAvailable = true;
    // }
  }

  initPayPal() {
    // WEB-5426: call /api/subscriptions/paypal/start if membership item
    const isSubscriptionOrder = this.extraData.order_items.find((oi) => oi.purchased_entity.type === 'membership');
    if (isSubscriptionOrder) {
      this.payPalConfig.intent = 'subscription';
      this.payPalConfig.vault = 'true';
      this.payPalConfig.createSubscriptionOnClient = () => ({
        custom_id: this.extraData.order_id.toString(),
        plan_id: this.extraData.order_items[0].purchased_entity.field_paypal_plan_id,
      });
      this.payPalConfig.onApprove = (data) => {
        this.startPaypalSubscription(data.subscriptionID);
      };
      return;
    }

    // Normal order flow
    const currency: string = this.requestOptions.currency.toUpperCase();
    if (!currency) {
      return;
    }
    this.payPalConfig.currency = currency;
    this.payPalConfig.onClientAuthorization = (data) => {
      this.paypalClientAuth(data);
    };
    this.payPalConfig.createOrderOnClient = () => ({
      intent: 'CAPTURE',
      purchase_units: [
        {
          amount: {
            currency_code: currency,
            value: `${this.customService.replaceCommaWithDot(this.extraData.total_price.number)}`,
            breakdown: {
              item_total: {
                currency_code: currency,
                value: `${this.customService.replaceCommaWithDot(this.extraData.total_price.number)}`,
              },
            },
          },
          items: this.extraData.order_items.map((product) => {
            const item: ITransactionItem = {
              name: `${product.purchased_entity.title} - ${product.purchased_entity.sku}`,
              quantity: Math.round(+(product.quantity ?? '1')).toString(),
              unit_amount: {
                currency_code: currency,
                value: this.customService.replaceCommaWithDot(product.unit_price.number?.toString()) as any,
              },
            };
            return item;
          }),
        },
      ],
    });
  }

  payment(): void {
    this.trackingService.trackEvent({
      event: 'payment',
      dl_name: 'payment_title',
      dl_value: `Payment ${this.requestOptions.currency} ${this.extraData.total_price.number}`,
    });
  }

  onCookieStatusChange(status: boolean): void {
    if (status === true) {
      this.stripeService = this.injector.get(StripeService);
      // fix the lazy load of the stripe service
      this.stripeService.loader.asStream().subscribe((loader) => {
        if (loader.loaded) {
          this.initStripe();
        }
      });
    }
  }

  private logError(error: any, type: string): void {
    this.loggerService.logError(error.toString() + ` | payment: ${type} | url: ${window.location?.href}`);
  }
}
